import type { DefaultCellTypes } from "@silevis/reactgrid";

export const WEEKLY_PLANNER_ROW_HEIGHT = 30;
export const WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT = 300;
export const PROJECT_COLUMN_WIDTH = 320;
export const CHARS_PER_LINE = 38;
export const LINE_HEIGHT = 20;
export const BASE_ROW_PADDING = 10;
export const GROUP_HEADING_CELL_CLASS = "rg-group-heading-cell";
export const METRIC_CELL_CLASS = "rg-metric-cell";
export const ROLLUP_ROW_CELL_CLASS = "rg-rollup-row-cell";
export const PROJECT_ROW_EVEN_CELL_CLASS = "rg-project-row-even";
export const PROJECT_ROW_ODD_CELL_CLASS = "rg-project-row-odd";
export const PROJECT_ROW_DRAGGABLE_LABEL_CLASS = "rg-project-row-draggable-label";
export const PROJECT_ROW_DRAGGING_CLASS = "rg-project-row-dragging";
export const PROJECT_ROW_DROP_TARGET_TOP_CLASS = "rg-project-row-drop-target-top";
export const PROJECT_ROW_DROP_TARGET_BOTTOM_CLASS = "rg-project-row-drop-target-bottom";
export const DEVELOPER_COLUMN_DRAGGING_CLASS = "rg-developer-column-dragging";
export const DEVELOPER_COLUMN_DROP_TARGET_LEFT_CLASS = "rg-developer-column-drop-target-left";
export const DEVELOPER_COLUMN_DROP_TARGET_RIGHT_CLASS = "rg-developer-column-drop-target-right";
export const COLLAB_ACTIVE_CELL_CLASS = "rg-collab-active-cell";
export const EDITABLE_HOURS_CELL_CLASS = "rg-editable-hours-cell";
export const READONLY_HOURS_CELL_CLASS = "rg-readonly-hours-cell";
export const AVAILABLE_OVERRIDE_CELL_CLASS = "rg-available-override-cell";
export const HOURS_LEFT_CELL_CLASS = "rg-hours-left-cell";
export const HOURS_LEFT_SAFE_CELL_CLASS = "rg-hours-left-safe";
export const HOURS_LEFT_WARNING_CELL_CLASS = "rg-hours-left-warning";
export const HOURS_LEFT_OVER_CELL_CLASS = "rg-hours-left-over";
export const HOURS_BALANCE_CELL_CLASS = "rg-hours-balance-cell";
export const HOURS_BALANCE_SAFE_CELL_CLASS = "rg-hours-balance-safe";
export const HOURS_BALANCE_WARNING_CELL_CLASS = "rg-hours-balance-warning";
export const HOURS_BALANCE_OVER_CELL_CLASS = "rg-hours-balance-over";
export const MY_HOURS_CELL_CLASS = "rg-my-hours-cell";
export const OTHER_HOURS_CELL_CLASS = "rg-other-hours-cell";

export function calculateRowHeight(text: string): number {
  const estimatedLines = Math.max(1, Math.ceil(text.length / CHARS_PER_LINE));
  return Math.max(WEEKLY_PLANNER_ROW_HEIGHT, BASE_ROW_PADDING + estimatedLines * LINE_HEIGHT);
}

export function safeNumber(value: unknown, decimals = 1): number {
  if (value === null || value === undefined) return 0;
  const num = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(num)) return 0;

  const quarterHours = Math.round(num * 4) / 4;
  if (Math.abs(num - quarterHours) < 0.000001) return quarterHours;

  return Number(num.toFixed(decimals));
}

export function displayNumber(value: unknown, decimals = 1): string {
  return safeNumber(value, decimals).toString();
}

export function hoursLeftThresholdClass(
  hoursLeft: number | string | null | undefined,
  totalHours: number | string | null | undefined,
): string {
  const remaining = safeNumber(hoursLeft, 1);
  const total = safeNumber(totalHours, 1);

  if (remaining <= 0) return HOURS_LEFT_OVER_CELL_CLASS;
  if (total > 0 && remaining / total <= 0.1) return HOURS_LEFT_WARNING_CELL_CLASS;
  return HOURS_LEFT_SAFE_CELL_CLASS;
}

export function hoursIdentityClass(
  currentUserId: number | null | undefined,
  developerId: number,
): string {
  if (!currentUserId) return OTHER_HOURS_CELL_CLASS;
  return currentUserId === developerId ? MY_HOURS_CELL_CLASS : OTHER_HOURS_CELL_CLASS;
}

export function hoursBalanceThresholdClass(
  actualHours: number | string | null | undefined,
  plannedHours: number | string | null | undefined,
): string {
  const actual = safeNumber(actualHours, 1);
  const planned = safeNumber(plannedHours, 1);

  if (planned <= 0)
    return actual > 0 ? HOURS_BALANCE_OVER_CELL_CLASS : HOURS_BALANCE_SAFE_CELL_CLASS;
  const deviationRatio = (actual - planned) / planned;
  if (deviationRatio >= 0.1) return HOURS_BALANCE_OVER_CELL_CLASS;
  if (deviationRatio <= -0.1) return HOURS_BALANCE_WARNING_CELL_CLASS;
  return HOURS_BALANCE_SAFE_CELL_CLASS;
}

export function joinClasses(...classNames: Array<string | undefined>): string | undefined {
  const normalized = classNames
    .filter((className): className is string => Boolean(className))
    .join(" ");
  return normalized.length > 0 ? normalized : undefined;
}

export function asReadOnly<T extends DefaultCellTypes>(cell: T): T {
  return { ...cell, nonEditable: true };
}
